import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { DietaryTagModalComponent } from 'src/app/dietary-tag-modal/dietary-tag-modal.component';
import { ReservationTag, ReservationTagCategory } from 'src/app/model/reservation-tag';
import { TagType } from 'src/app/model/tag-type';
import { ReservationTagModalComponent } from 'src/app/reservation-tag-modal/reservation-tag-modal.component';
import * as AppSelectors from '../../store/selectors/app.selectors';

@Component({
  selector: 'app-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss']
})
export class TagsComponent implements OnInit, OnDestroy {

  constructor(
    private store: Store<{}>,
    private translate: TranslateService
  ) { }

  destroy$: Subject<boolean> = new Subject<boolean>();

  @ViewChild('reservationTagModal') reservationTagModal!: ReservationTagModalComponent;
  @ViewChild('dietaryTagModal') dietaryTagModal!: DietaryTagModalComponent;

  @Input() readOnlyMode: boolean = false;

  @Input() tagCategories: ReservationTagCategory[] = [];
  @Input() modalId: string = 'modalId';
  @Input() tagType: TagType = 'RESERVATION';

  _selectedTags: ReservationTag[] | undefined = [];
  @Input() set selectedTags(value: ReservationTag[] | undefined) {
    this._selectedTags = JSON.parse(JSON.stringify(value));
  }

  @Input() displayCategoryLabel: boolean = true;
  @Input() displayAdditionalTextInput: boolean = false;
  @Input() additionalTextInput: string | undefined = '';

  @Output() setTags = new EventEmitter<ReservationTag[]>();
  @Output() setAdditionalTextInput = new EventEmitter<string>();

  lang: string = 'en';

  tags$: Observable<ReservationTagCategory[] | null> = this.store.pipe(select(AppSelectors.selectTags));
  allTags: ReservationTagCategory[] = [];
  searchReservationTagsTerm: string = '';
  reservationTag: ReservationTag = new ReservationTag();

  ngOnInit(): void {

    this.lang = this.translate.currentLang;

    this.tags$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      this.allTags = tags ?? [];
    });

  }

  getTagColor(tag: ReservationTag) {
    return this.allTags?.find(tagCategory => tagCategory.id === tag.categoryId)?.color;
  }

  filterReservationTags(tag: ReservationTag, ...args: any[]) {
    const searchReservationTagsTerm: string = args[0][0];

    return tag.title.toLowerCase().indexOf(searchReservationTagsTerm.toLowerCase().trim()) >= 0;
  }

  showAddReservationTagModal() {
    if (this.readOnlyMode) {
      return;
    }

    this.reservationTag = new ReservationTag();

    if (this.tagCategories.length === 1) {
      this.reservationTag.categoryId = this.tagCategories[0].id;
    }

    this.reservationTag.tagOrder = undefined;

    this.reservationTagModal.show(this.reservationTag);
  }

  reservationTagModalClosed(updates: ReservationTag): void {
    if (this.readOnlyMode) {
      return;
    }
    // todo: update model
  }

  selectedTagsNumberByCategory(tagCategory: ReservationTagCategory) {
    return this._selectedTags?.filter(tag => tag.categoryId === tagCategory.id).length;
  }

  hasReservationTag(tag: ReservationTag) {
    return this._selectedTags?.find(t => t.id == tag.id) != null;
  }

  toggleSelectReservationTag(tag: ReservationTag) {
    if (this._selectedTags == null) {
      this._selectedTags = [tag];
      this.setReservationTags();
      return;
    }

    const index = this._selectedTags.findIndex(t => t.id === tag.id);
    if (index >= 0) {
      this._selectedTags.splice(index, 1);
    } else {
      this._selectedTags.push(tag);
    }
    this.setReservationTags();
  }

  setReservationTags() {
    this.setTags.emit(this._selectedTags);
  }

  setAdditionalText(event: Event) {
    const text = (event.target as HTMLInputElement).value;
    this.setAdditionalTextInput.emit(text);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
